import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { api, prefetchGet } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { num, fmtDate, SEVERITY } from "../lib/format";
import { SeverityBadge } from "../components/badges";
import { Skeleton } from "../components/ui/skeleton";
import { CustomerLogo } from "../components/CustomerLogo";
import { CustomerTabs } from "../components/CustomerTabs";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell,
} from "recharts";
import { History, UserPlus, UserMinus, ChevronRight, ChevronDown, ArrowUpRight, ArrowDownRight, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUS = {
  OPEN: { label: "Open", cls: "bg-warn/15 text-warn border-warn/30" },
  IN_REVIEW: { label: "In Review", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  COMPLETE: { label: "Complete", cls: "bg-pos/15 text-pos border-pos/30" },
};
const sevBorder = (s) => (SEVERITY[s] || SEVERITY[1]).rail;
const sevTile = (s) => (Number(s) >= 4 ? "border-neg/40 bg-neg/10 hover:bg-neg/15" : "border-border bg-card hover:bg-card-hover");
const money = (v, ccy) => (v == null ? "—" : `${ccy} ${num(v, 0)}`);

function TurnoverPanel({ kind, count, list, ccy, expanded, onToggle }) {
  const isHire = kind === "hires";
  const Icon = isHire ? UserPlus : UserMinus;
  const iconCls = isHire ? "bg-pos/15 text-pos" : "bg-neg/15 text-neg";
  const numCls = isHire ? "text-pos" : "text-neg";
  const dateKey = isHire ? "hire_date" : "termination_date";
  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        data-testid={`turnover-toggle-${kind}`}
        onClick={onToggle}
        disabled={!count}
        className="flex w-full items-center gap-4 p-5 text-left disabled:cursor-default"
      >
        <div className={`grid h-12 w-12 place-items-center rounded-full ${iconCls}`}><Icon size={22} /></div>
        <div>
          <div className={`font-mono text-3xl font-black ${numCls}`} data-testid={`turnover-${kind}-count`}>{num(count)}</div>
          <div className="text-sm font-semibold text-muted-foreground">{isHire ? "Hires" : "Terms"} this compare</div>
        </div>
        {count > 0 ? <ChevronDown size={18} className={`ml-auto text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} /> : null}
      </button>
      {expanded && count > 0 && (
        <div className="max-h-64 overflow-auto border-t border-border" data-testid={`turnover-list-${kind}`}>
          {list.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No employee detail available.</div>
          ) : (
            list.map((e, i) => (
              <div key={e.payroll_employee_GUID || i} className="flex items-center justify-between gap-3 border-b border-border/50 px-5 py-2.5 last:border-0">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{e.employee_name_as_printed}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">ID {e.provider_employee_id || "—"}{e.department_as_printed ? ` · ${e.department_as_printed}` : ""}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-mono text-sm">{e.gross_pay_amount != null ? money(e.gross_pay_amount, ccy) : "—"}</div>
                  {e[dateKey] ? <div className="font-mono text-[11px] text-muted-foreground">{isHire ? "Hired" : "Termed"} {fmtDate(e[dateKey])}</div> : null}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CompareBars({ data, ccy, logScale }) {
  const clamp = (v) => (logScale ? Math.max(Math.abs(v || 0), 1) : (v || 0));
  const rows = data.map((d) => ({
    name: d.label, unit: d.unit, _p: d.previous, _c: d.current,
    Prior: clamp(d.previous), Current: clamp(d.current),
  }));
  const fmtVal = (orig, unit) => (unit === "hours" ? `${num(orig, 1)} h` : `${ccy} ${num(orig, 0)}`);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, bottom: 4, left: 12 }} barGap={2}>
        <XAxis
          type="number" tick={{ fontSize: 11 }} stroke="currentColor" className="text-muted-foreground"
          {...(logScale ? { scale: "log", domain: [1, "auto"], allowDataOverflow: true } : {})}
          tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : num(v, 0))}
        />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} stroke="currentColor" className="text-muted-foreground" />
        <Tooltip
          cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }}
          content={({ active, payload }) => {
            if (!active || !payload || !payload.length) return null;
            const p = payload[0].payload;
            const d = (p._c || 0) - (p._p || 0);
            const pct = p._p ? (d / Math.abs(p._p)) * 100 : null;
            const flat = Math.abs(d) < 0.005;
            const dcolor = flat ? "text-muted-foreground" : d > 0 ? "text-pos" : "text-neg";
            const sign = d > 0 ? "+" : "";
            return (
              <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md" data-testid="chart-tooltip">
                <div className="mb-1 font-semibold text-foreground">{p.name}</div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Prior</span><span className="font-mono">{fmtVal(p._p, p.unit)}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Current</span><span className="font-mono">{fmtVal(p._c, p.unit)}</span></div>
                <div className={`mt-1 flex justify-between gap-4 border-t border-border pt-1 font-semibold ${dcolor}`}>
                  <span>Delta</span>
                  <span className="font-mono">{sign}{p.unit === "hours" ? `${num(d, 1)} h` : `${ccy} ${num(d, 0)}`}{pct == null ? "" : ` (${sign}${pct.toFixed(1)}%)`}</span>
                </div>
              </div>
            );
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="Prior" fill="#475569" radius={[0, 3, 3, 0]} />
        <Bar dataKey="Current" fill="#38BDF8" radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function VarPct({ cur, prev }) {
  if (prev == null || prev === 0) return <span className="text-muted-foreground text-xs">no prior</span>;
  const d = cur - prev;
  const pct = (d / Math.abs(prev)) * 100;
  const up = d > 0;
  const flat = Math.abs(d) < 0.005;
  const color = flat ? "text-muted-foreground" : up ? "text-pos" : "text-neg";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
      {!flat && <Icon size={13} />}
      {up ? "+" : ""}{num(d, 0)} ({up ? "+" : ""}{pct.toFixed(1)}%)
    </span>
  );
}

function Kpi({ testid, label, cur, prev, ccy }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-border bg-card p-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-2xl font-bold tracking-tight">{money(cur, ccy)}</div>
      <div className="mt-1"><VarPct cur={cur} prev={prev} /></div>
      <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">prior {money(prev, ccy)}</div>
    </div>
  );
}

export default function ClientSummary() {
  const { clientId } = useParams();
  const qc = useQueryClient();
  const [runSel, setRunSel] = useState(null);
  const [expand, setExpand] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["compare", clientId, runSel],
    queryFn: async () =>
      (await api.get(`/clients/${clientId}/compare${runSel ? `?run=${runSel}` : ""}`)).data,
    keepPreviousData: true,
  });

  if (isLoading)
    return (
      <Layout>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-20 rounded-lg" />
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <Skeleton key={`k-${i}`} className="h-28 rounded-lg" />)}
        </div>
        <Skeleton className="mt-6 h-80 rounded-lg" />
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="rounded-md border border-neg/30 bg-neg/5 p-6 text-neg">
          {error.response?.status === 403 ? "You do not have access to this customer." : "Failed to load customer."}
        </div>
      </Layout>
    );

  const { client, currency: ccy, runs, current, previous, kpis, categories, turnover, added_employees, dropped_employees, earnings_breakdown, hours_breakdown, has_hours, deductions_breakdown, taxes_breakdown, priority_cards, status, open_issues, total_issues, closed_issues } = data;
  const st = STATUS[status] || STATUS.OPEN;
  const chartData = categories.map((c) => ({ name: c.label, Prior: c.previous, Current: c.current }));
  const combined = [
    ...(earnings_breakdown || []).map((e) => ({ label: e.label, current: e.current, previous: e.previous, unit: "money" })),
    ...(hours_breakdown || []).map((h) => ({ label: h.label, current: h.current, previous: h.previous, unit: "hours" })),
  ];
  const dedData = (deductions_breakdown || []).map((e) => ({ label: e.label, current: e.current, previous: e.previous, unit: "money" }));
  const taxData = (taxes_breakdown || []).map((e) => ({ label: e.label, current: e.current, previous: e.previous, unit: "money" }));

  return (
    <Layout>
      <Breadcrumbs items={[{ label: "Portfolio", to: "/" }, { label: client.client_name }]} />

      {/* Header */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
        <div className="flex items-center gap-4">
          <CustomerLogo clientId={clientId} hasLogo={client.has_logo} size={56} iconSize={26} />
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black">{client.client_name}</h1>
            <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground">
              {client.client_short_name}
              {client.provider_company_code ? ` · Co ${client.provider_company_code}` : ""}
              {" · "}{client.country_code} · {ccy}
            </p>
          </div>
        </div>
        {client.has_audit_feed === false ? (
          <div data-testid="audit-unavailable" title="No audit feed has been received for this customer yet"
            className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            <History size={16} /> No audit feed yet
          </div>
        ) : (
          <Button asChild variant="outline" className="gap-2" data-testid="audit-trail-btn">
            <Link to={`/clients/${clientId}/audit`}><History size={16} /> Audit Trail</Link>
          </Button>
        )}
      </div>

      <CustomerTabs clientId={clientId} active="charts" />

      {!current ? (
        <div className="mt-8 rounded-md border border-border bg-card p-8 text-center text-muted-foreground">
          No payroll runs on file for this customer yet.
        </div>
      ) : (
        <>
          {/* Compare selector bar */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card/40 p-5">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Payroll Compare</div>
                <div className="mt-1 flex items-center gap-2 font-mono text-sm">
                  <span className="font-bold text-foreground">{current.run_label}</span>
                  <span className="text-muted-foreground">({fmtDate(current.pay_date)})</span>
                  <span className="text-border">vs</span>
                  {previous ? (
                    <span className="text-muted-foreground">{previous.run_label} ({fmtDate(previous.pay_date)})</span>
                  ) : (
                    <span className="text-muted-foreground">no prior run</span>
                  )}
                </div>
              </div>
              <Select value={current.payroll_run_GUID} onValueChange={setRunSel}>
                <SelectTrigger className="w-[260px]" data-testid="compare-run-selector">
                  <SelectValue placeholder="Choose a payroll compare" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {runs.map((r) => (
                    <SelectItem key={r.payroll_run_GUID} value={r.payroll_run_GUID} data-testid={`compare-run-${r.run_label}`}>
                      {r.run_label} · {fmtDate(r.pay_date)}{(r.run_type_code || "").toUpperCase() === "REGULAR" ? "" : ` · ${r.run_type_code}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                <span className="font-mono font-bold text-foreground">{num(open_issues)}</span> open · {num(closed_issues)} reviewed
              </span>
              <span data-testid="compare-status-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${st.cls}`}>
                {status === "COMPLETE" ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {st.label}
              </span>
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi testid="kpi-gross" label="Gross" cur={kpis.gross.current} prev={kpis.gross.previous} ccy={ccy} />
            <Kpi testid="kpi-net" label="Net" cur={kpis.net.current} prev={kpis.net.previous} ccy={ccy} />
            <Kpi testid="kpi-earnings" label="Earnings" cur={kpis.earnings.current} prev={kpis.earnings.previous} ccy={ccy} />
            <Kpi testid="kpi-deductions" label="Deductions" cur={kpis.deductions.current} prev={kpis.deductions.previous} ccy={ccy} />
            <Kpi testid="kpi-taxes" label="Taxes" cur={kpis.taxes.current} prev={kpis.taxes.previous} ccy={ccy} />
            <div data-testid="kpi-employees" className="rounded-lg border border-border bg-card p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Employees</div>
              <div className="mt-1 font-mono text-2xl font-bold tracking-tight">{num(kpis.employees.current)}</div>
              <div className="mt-1 text-xs font-semibold text-muted-foreground">
                {kpis.employees.previous != null ? `prior ${num(kpis.employees.previous)}` : "no prior"}
              </div>
            </div>
          </div>

          {/* Hires & Terms (expandable in place) */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TurnoverPanel kind="hires" count={turnover.hires} list={added_employees || []} ccy={ccy}
              expanded={expand === "hires"} onToggle={() => setExpand((v) => (v === "hires" ? null : "hires"))} />
            <TurnoverPanel kind="terms" count={turnover.terms} list={dropped_employees || []} ccy={ccy}
              expanded={expand === "terms"} onToggle={() => setExpand((v) => (v === "terms" ? null : "terms"))} />
          </div>

          {/* Category deltas: chart + table */}
          <section className="mt-8">
            <h2 className="font-heading text-lg font-bold mb-3">Category Deltas — Current vs Prior</h2>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
              <div className="lg:col-span-6 rounded-lg border border-border bg-card p-4" style={{ height: "22rem" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 20, bottom: 4, left: 12 }} barGap={2}>
                    <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} stroke="currentColor" className="text-muted-foreground" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} stroke="currentColor" className="text-muted-foreground" />
                    <Tooltip
                      cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }}
                      content={({ active, payload }) => {
                        if (!active || !payload || !payload.length) return null;
                        const p = payload[0].payload;
                        const d = (p.Current || 0) - (p.Prior || 0);
                        const pct = p.Prior ? (d / Math.abs(p.Prior)) * 100 : null;
                        const flat = Math.abs(d) < 0.005;
                        const dcolor = flat ? "text-muted-foreground" : d > 0 ? "text-pos" : "text-neg";
                        const sign = d > 0 ? "+" : "";
                        return (
                          <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md" data-testid="category-tooltip">
                            <div className="mb-1 font-semibold text-foreground">{p.name}</div>
                            <div className="flex justify-between gap-4"><span className="text-muted-foreground">Prior</span><span className="font-mono">{ccy} {num(p.Prior, 0)}</span></div>
                            <div className="flex justify-between gap-4"><span className="text-muted-foreground">Current</span><span className="font-mono">{ccy} {num(p.Current, 0)}</span></div>
                            <div className={`mt-1 flex justify-between gap-4 border-t border-border pt-1 font-semibold ${dcolor}`}>
                              <span>Delta</span>
                              <span className="font-mono">{sign}{ccy} {num(d, 0)}{pct == null ? "" : ` (${sign}${pct.toFixed(1)}%)`}</span>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Prior" fill="#475569" radius={[0, 3, 3, 0]} />
                    <Bar dataKey="Current" fill="#38BDF8" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="lg:col-span-6 rounded-lg border border-border bg-card overflow-hidden">
                <table className="w-full text-sm" data-testid="category-deltas-table">
                  <thead>
                    <tr className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3 text-right">Prior</th>
                      <th className="px-4 py-3 text-right">Current</th>
                      <th className="px-4 py-3 text-right">Delta</th>
                      <th className="px-4 py-3 text-right">Var %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((c) => {
                      const d = c.current - c.previous;
                      const pct = c.previous ? (d / Math.abs(c.previous)) * 100 : null;
                      const color = Math.abs(d) < 0.005 ? "text-muted-foreground" : d > 0 ? "text-pos" : "text-neg";
                      return (
                        <tr key={c.key} className="border-t border-border" data-testid={`category-row-${c.key}`}>
                          <td className="px-4 py-3 font-semibold">{c.label}</td>
                          <td className="px-4 py-3 text-right font-mono text-muted-foreground">{num(c.previous, 0)}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold">{num(c.current, 0)}</td>
                          <td className={`px-4 py-3 text-right font-mono font-semibold ${color}`}>{d > 0 ? "+" : ""}{num(d, 0)}</td>
                          <td className={`px-4 py-3 text-right font-mono ${color}`}>{pct == null ? "—" : `${d > 0 ? "+" : ""}${pct.toFixed(1)}%`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Earnings, Deductions & Taxes — same row */}
          <section className="mt-8">
            <h2 className="mb-3 font-heading text-lg font-bold">Earnings, Deductions &amp; Taxes — Current vs Prior</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-heading text-base font-bold">Top Earnings &amp; Hours</h3>
                  <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Log scale</span>
                </div>
                <div className="rounded-lg border border-border bg-card p-4" data-testid="earnings-hours-breakdown" style={{ height: "26rem" }}>
                  {combined.length ? (
                    <CompareBars data={combined} ccy={ccy} logScale />
                  ) : (
                    <div className="grid h-full place-items-center text-sm text-muted-foreground">No earnings or hours on this compare.</div>
                  )}
                </div>
              </div>
              <div>
                <h3 className="mb-2 font-heading text-base font-bold">Top Deductions</h3>
                <div className="rounded-lg border border-border bg-card p-4" data-testid="deductions-breakdown" style={{ height: "26rem" }}>
                  {dedData.length ? (
                    <CompareBars data={dedData} ccy={ccy} />
                  ) : (
                    <div className="grid h-full place-items-center text-sm text-muted-foreground">No deductions on this compare.</div>
                  )}
                </div>
              </div>
              <div>
                <h3 className="mb-2 font-heading text-base font-bold">Top Taxes</h3>
                <div className="rounded-lg border border-border bg-card p-4" data-testid="taxes-breakdown" style={{ height: "26rem" }}>
                  {taxData.length ? (
                    <CompareBars data={taxData} ccy={ccy} />
                  ) : (
                    <div className="grid h-full place-items-center text-sm text-muted-foreground">No taxes on this compare.</div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* High-priority issue cards */}
          <section className="mt-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold">High-Priority Items</h2>
              <Button asChild size="sm" data-testid="review-issues-btn">
                <Link to={`/runs/${current.payroll_run_GUID}`}>Review issues <ChevronRight size={16} /></Link>
              </Button>
            </div>
            {priority_cards.length === 0 ? (
              <div className="rounded-lg border border-pos/30 bg-pos/5 p-6 text-pos flex items-center gap-2">
                <CheckCircle2 size={18} /> No exceptions flagged for this payroll compare.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[...priority_cards].sort((a, b) => (Number(b.severity) - Number(a.severity)) || (b.n - a.n)).map((p) => (
                  <Link
                    key={p.check_code}
                    to={`/runs/${current.payroll_run_GUID}/errors?check=${encodeURIComponent(p.check_code)}`}
                    data-testid={`priority-card-${p.check_code}`}
                    onMouseEnter={() => prefetchGet(qc, ["run-errors", current.payroll_run_GUID], `/runs/${current.payroll_run_GUID}/errors`)}
                    className={`block rounded-lg border border-l-4 ${sevBorder(p.severity)} ${sevTile(p.severity)} p-4 transition-colors`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-bold leading-tight">{p.check_name}</div>
                      <SeverityBadge severity={p.severity} />
                    </div>
                    <div className="mt-2 flex items-end justify-between">
                      <span className="font-mono text-3xl font-black">{num(p.n)}</span>
                      {p.impact ? <span className="font-mono text-xs text-muted-foreground">{ccy} {num(p.impact, 0)}</span> : null}
                    </div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{p.check_code}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </Layout>
  );
}
